// src/services/workItemSeenService.js
//
// Universal Work Item "Seen" API client
// =====================================
//
// PURPOSE
// -------
// This service sends the explicit "seen" signal for a work item.
//
// In your current application, "seen" means:
//
//   - the current user has opened the work item at least once
//   - Dashboard "New" badge should disappear for that user
//
// Backend persistence model:
// - WorkItemRead.first_seen_at
//
// IMPORTANT SEMANTIC SPLIT
// ------------------------
// In your architecture, "seen" is NOT the same as "chat read".
//
// - seen:
//     controls Inbox "New" semantics
//     -> WorkItemRead.first_seen_at
//
// - chat read:
//     controls unread chat semantics
//     -> WorkItemRead.last_read_at
//
// This service is ONLY responsible for the "seen" part.
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// This service intentionally stays very thin:
//
// - apiClient.js
//     owns auth headers, cookies, refresh-on-401, session selector
//
// - AuthContext.js / pages
//     decide what to do on auth failure (for example softLogout)
//
// - backend
//     remains authoritative for idempotency and permission checks
//
// Therefore this file should:
// - validate the work item key
// - send the HTTP request
// - return backend data as-is
// - rethrow errors to the caller
//
// IMPORTANT ERROR POLICY
// ----------------------
// This service does NOT swallow normal request failures.
//
// Why?
// - UI/pages/components must stay in control of behavior
// - some callers may want:
//     - silent best-effort behavior
//     - softLogout on 401
//     - toast error
//     - retry logic
//
// So this service:
// - ignores cancellation for logging purposes
// - but still rethrows the error
//
// ENDPOINT CONTRACT
// -----------------
//   POST /work-items/:key/seen
//
// Expected backend behavior:
// - create WorkItemRead row if missing
// - set first_seen_at only if currently NULL
// - remain idempotent across retries / repeated opens
//
// Typical response shape:
//   {
//     ok: true,
//     workItemKey: "todo:123",
//     first_seen_at: "2026-03-28T18:00:00.000Z"
//   }

import apiClient from "../utils/apiClient";
import { isRequestCanceled, assertWorkItemKey } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Build the backend endpoint for marking a work item as seen.
 *
 * We encode the universal work item key because it contains ":" and may
 * contain additional safe characters in future supported key formats.
 *
 * Example:
 *   "todo:123"  -> "/work-items/todo%3A123/seen"
 *
 * @param {string} workItemKey
 * @returns {string}
 */
function buildSeenEndpoint(workItemKey) {
  return `/work-items/${encodeURIComponent(workItemKey)}/seen`;
}

/* -------------------------------------------------------------------------- */
/* API                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Mark a work item as seen for the current authenticated user.
 *
 * IMPORTANT:
 * - This function is transport-only.
 * - It does NOT implement UI fallback behavior.
 * - It does NOT call softLogout().
 * - It does NOT suppress real failures.
 *
 * Caller responsibilities:
 * - if used as best-effort, call it and ignore failure intentionally
 * - if used in a page that should react to 401, handle that in the caller
 *
 * Example best-effort usage:
 *   markWorkItemSeen(key).catch(() => {});
 *
 * Example strict usage:
 *   await markWorkItemSeen(key);
 *
 * @param {string} workItemKey
 *   Universal work item key, for example:
 *   - "todo:123"
 *   - "leave:9223372036854775807"
 *
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 *   Optional AbortController signal for request cancellation.
 *
 * @returns {Promise<any>}
 *   Resolves with backend response payload.
 *
 * @throws {Error}
 *   Rethrows all non-cancellation errors.
 */
export async function markWorkItemSeen(workItemKey, options = {}) {
  const key = assertWorkItemKey(workItemKey);

  try {
    const response = await apiClient.post(
      buildSeenEndpoint(key),
      {},
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return response.data;
  } catch (error) {
    /**
     * Cancellation is normal control flow:
     * - route changed
     * - component unmounted
     * - request got superseded
     *
     * We do not log those as errors.
     */
    if (!isRequestCanceled(error)) {
      console.error("[WorkItemSeenService] ❌ markWorkItemSeen failed:", error);
    }

    throw error;
  }
}

export default {
  markWorkItemSeen,
};